import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCompetitionClubs } from "../../../api/admin";
import { Club } from "../../../api/clubs";
import { computePositionBands } from "../../../lib/fpBands";
import FpDragRow from "../../league/FpDragRow";
import { card } from "../../../styles";

interface Props {
  user: User;
  competitionId: string;
  onChange: (predictions: { clubId: string; position: number }[]) => void;
  /** Prefills the table with the member's real current order when editing an existing
   * member's predictions — omitted for the add-member backfill flow, which starts
   * alphabetical (there's nothing real to prefill for a brand-new member). */
  initialPredictions?: { clubId: string; position: number }[];
}

/**
 * Admin-only table picker for backfilling a FINAL_POSITIONS member's predicted table
 * after their deadline has passed, or editing an existing member's table mid-season —
 * reuses FpDragRow (frontend/src/components/league/FpDragRow.tsx), the same drag-to-rank
 * row every member-facing FP screen uses. Starts every club in mid-table (no
 * admin-configured top-tier/relegation split is fetched here — a cosmetic simplification;
 * the backend still enforces a full, valid permutation regardless of band labels shown
 * here) unless `initialPredictions` is given, in which case that real order is used and no
 * alphabetical default is ever emitted.
 */
export default function FinalPositionsTablePicker({ user, competitionId, onChange, initialPredictions }: Props) {
  const { t } = useTranslation();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [order, setOrder] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const dragIndexRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCompetitionClubs(user, competitionId)
      .then((res) => {
        if (cancelled) return;
        setClubs(res);
        if (initialPredictions && initialPredictions.length > 0) {
          const positionByClub = new Map(initialPredictions.map((p) => [p.clubId, p.position]));
          const ordered = [...res].sort((a, b) => (positionByClub.get(a.id) ?? Infinity) - (positionByClub.get(b.id) ?? Infinity));
          setOrder(ordered);
          // Deliberately no onChange call here — the incoming order already matches what
          // the parent holds, and emitting again would be a needless re-render, not a
          // correctness requirement (unlike the alphabetical-default branch below, which
          // must announce its default since the parent starts with nothing).
        } else {
          const sorted = [...res].sort((a, b) => a.name.localeCompare(b.name));
          setOrder(sorted);
          onChange(sorted.map((c, i) => ({ clubId: c.id, position: i + 1 })));
        }
      })
      .catch(() => { /* ignore — empty list surfaces as a backend validation error on submit */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, competitionId]);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      onChange(next.map((c, i) => ({ clubId: c.id, position: i + 1 })));
      return next;
    });
  };

  const bands = computePositionBands({
    clubCount: clubs.length,
    topTierSize: null,
    relegationSize: null,
    championEnabled: false,
    topTierLabel: null,
    relegationLabel: null,
    fallbackLabels: { champion: t("prediction.bands.champion"), top: t("prediction.bands.top"), mid: t("prediction.bands.mid"), relegation: t("prediction.bands.relegation") },
  });

  if (loading) return <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>;

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      {order.map((c, i) => (
        <FpDragRow
          key={c.id}
          index={i}
          total={order.length}
          label={c.shortName ?? c.name}
          band={bands[i]}
          dragIndexRef={dragIndexRef}
          move={move}
          isLast={i === order.length - 1}
        />
      ))}
    </div>
  );
}
